import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { format } from 'date-fns';

import { ILicense } from 'interfaces/license.interface';
import { downloadLicense } from 'requests/license.request';
import Button from 'components/button/button.component';
import Icon from 'components/icon/icon.component';
import styles from '../licenses.module.scss';

interface IProps {
  license: ILicense;
}

const ViewLicenseModal: React.FC<IProps> = ({ license }) => {
  const { t } = useTranslation();

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    try {
      return format(new Date(dateStr), 'dd.MM.yyyy');
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr: string) => {
    if (!dateStr) return '';
    try {
      return format(new Date(dateStr), 'dd.MM.yyyy HH:mm');
    } catch {
      return dateStr;
    }
  };

  const handleDownload = async () => {
    try {
      const blob = await downloadLicense(license.id);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `license_${license.clientId}.lic`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error(t('licenses.downloadError'));
    }
  };

  const Field = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div style={{ display: 'flex', gap: '0.5rem', padding: '0.5rem 0', borderBottom: '1px solid var(--color-light-gray)' }}>
      <strong style={{ minWidth: '160px', fontSize: '0.875rem' }}>{label}:</strong>
      <span style={{ fontSize: '0.875rem' }}>{value}</span>
    </div>
  );

  return (
    <div>
      <Field label={t('licenses.clientId')} value={license.clientId} />
      <Field label={t('licenses.type')} value={license.type} />
      <Field
        label={t('licenses.status')}
        value={
          <span
            className={
              license.status === 'ACTIVE'
                ? styles.licenses__statusActive
                : styles.licenses__statusRevoked
            }
          >
            {t(`licenses.statuses.${license.status}`)}
          </span>
        }
      />
      <Field label={t('licenses.maxEndpoints')} value={license.maxEndpoints} />
      <Field label={t('licenses.startDate')} value={formatDate(license.startDate)} />
      <Field label={t('licenses.endDate')} value={formatDate(license.endDate)} />
      <Field label={t('licenses.modules')} value={license.modules} />
      {license.hwid && <Field label={t('licenses.hwid')} value={license.hwid} />}
      <Field label={t('licenses.createdDate')} value={formatDateTime(license.createdDate)} />

      <div style={{ marginTop: '1rem' }}>
        <label style={{ fontWeight: 600, fontSize: '0.875rem', display: 'block', marginBottom: '0.5rem' }}>
          {t('licenses.licenseContent')}
        </label>
        <pre
          style={{
            background: '#1e1e1e',
            color: '#d4d4d4',
            padding: '1rem',
            borderRadius: '0.5rem',
            fontSize: '0.75rem',
            overflow: 'auto',
            maxHeight: '200px',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
          }}
        >
          {license.licenseContent}
        </pre>
      </div>

      <div className="modal__footer">
        <Button primary onClick={handleDownload}>
          <Icon name="Download" size={16} />
          {t('licenses.download')}
        </Button>
      </div>
    </div>
  );
};

export default ViewLicenseModal;
